import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { logoBg } from '../utils/logoBg';

const logoSrc = (url) =>
  url && url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;

const SuggestionLogo = ({ item }) => {
  const [failed, setFailed] = useState(false);
  if (!item.logo_url || failed) {
    return (
      <span className="h-7 w-7 shrink-0 rounded-md bg-orange-50 text-orange-600 text-xs font-bold flex items-center justify-center">
        {(item.name || '?').charAt(0).toUpperCase()}
      </span>
    );
  }
  return (
    <img src={logoSrc(item.logo_url)} alt="" onError={() => setFailed(true)}
      className={`h-7 w-7 shrink-0 rounded-md object-contain ${logoBg(item, 'bg-slate-50')}`} />
  );
};

export const SearchSuggestions = ({ query, open, onPick, testIdPrefix = 'search-suggest' }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [doneFor, setDoneFor] = useState('');
  const [active, setActive] = useState(-1);
  const reqId = useRef(0);
  const q = (query || '').trim();

  useEffect(() => {
    if (q.length < 2) { setItems([]); return undefined; }
    const id = ++reqId.current;
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get('/search', { params: { q, limit: 18 } });
        if (id !== reqId.current) return;
        const all = [
          ...(data.vendors || []).map((v) => ({ ...v, path: `/vendor/${v.id}` })),
          ...(data.emrs || []).map((e) => ({ ...e, path: `/emr/${e.id}` })),
        ];
        const lq = q.toLowerCase().replace(/\s+/g, '');
        const score = (n) => {
          const name = (n.name || '').toLowerCase().replace(/\s+/g, '');
          return name.startsWith(lq) ? 0 : name.includes(lq) ? 1 : 2;
        };
        all.sort((a, b) => score(a) - score(b) || a.name.localeCompare(b.name));
        setItems(all.slice(0, 6));
        setActive(-1);
        setDoneFor(q);
      } catch {
        if (id === reqId.current) { setItems([]); setDoneFor(q); }
      }
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  const visible = open && q.length >= 2 && items.length > 0;

  useEffect(() => {
    if (!visible) return undefined;
    const onKey = (e) => {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const step = e.key === 'ArrowDown' ? 1 : -1;
        const n = items.length + 1;
        setActive((i) => ((i + 1 + step + n) % n) - 1);
      } else if (e.key === 'Enter' && active >= 0) {
        e.preventDefault();
        e.stopPropagation();
        onPick?.();
        navigate(items[active].path);
      } else if (e.key === 'Escape') {
        setActive(-1);
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [visible, items, active, navigate, onPick]);

  if (open && q.length >= 2 && items.length === 0 && doneFor === q) {
    return (
      <div className="absolute left-0 right-0 top-full mt-1 z-50 min-w-[16rem] rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-500 shadow-lg" data-testid={`${testIdPrefix}-empty`}>
        No matching solutions for “{q}” — press Enter to search everything.
      </div>
    );
  }
  if (!visible) return null;

  return (
    <ul
      className="absolute left-0 right-0 top-full mt-1 z-50 min-w-[16rem] rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
      data-testid={`${testIdPrefix}-list`}
      role="listbox"
    >
      {items.map((it, i) => (
        <li key={it.path}>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => { onPick?.(); navigate(it.path); }}
            onMouseEnter={() => setActive(i)}
            aria-selected={i === active}
            role="option"
            className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-orange-50 focus:outline-none transition-colors ${i === active ? 'bg-orange-50' : ''}`}
            data-testid={`${testIdPrefix}-item-${it.id}`}
          >
            <SuggestionLogo item={it} />
            <span className="min-w-0">
              <span className="block text-sm font-medium text-gray-900 truncate">{it.name}</span>
              {it.vendor && it.vendor !== it.name && (
                <span className="block text-xs text-gray-500 truncate">by {it.vendor}</span>
              )}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
};
